import { DASH, M3U8, MP4, WEBM } from 'constants/mimes';
import { getDashPlaylistWithCredentials } from '../../../utilities/dash';
import { getHlsPlaylistWithCredentials } from '../../../utilities/hls';
import { PartCredentialType } from '../../../utilities/part-credentials';
import * as videoUtils from '../../../utilities/video';

const mimeTypeToPlaylistBuilderMap = {
  [M3U8]: getHlsPlaylistWithCredentials,
  [DASH]: getDashPlaylistWithCredentials,
};
const extensionToMimeMap = {
  mp4: MP4,
  webm: WEBM,
  m3u8: M3U8,
  mpd: DASH,
};
const getMimeTypeFromSourceUrl = (sourceUrl: string) => {
  const a = document.createElement('a');

  a.href = sourceUrl;
  const extension = a.pathname.split('.').pop();
  // @ts-expect-error ignore due to enabling strict null checks
  const mimeType = extensionToMimeMap[extension];

  if (!mimeType) {
    throw Error('Unrecognized extension: ' + extension);
  }

  return mimeType;
};

type PlaylistInfo = {
  sourceUrl: string;
  plainSourceUrl: string;
  partCredentials: Partial<PartCredentialType>;
  sourceMimeType: string;
  sourceUrlIsTranscoded: boolean;
};

export const getPlaylistInfo = (
  videoId: string,
  password: string,
  forceOriginal: boolean,
  forceMp4: boolean
): Promise<PlaylistInfo> => {
  const requestRaw = forceOriginal || !forceMp4;

  let getUrlHandler = videoUtils.postSessionRawURL;
  let supportedMimeTypes: string[] | undefined =
    videoUtils.getSupportedMimeTypes();

  if (!requestRaw) {
    supportedMimeTypes = undefined;
    getUrlHandler = videoUtils.postSessionTranscodedURL;
  }

  const parsePlaylistInfo =
    (resolve: (value: PlaylistInfo) => void) =>
    async (
      plainSourceUrl: string,
      partCredentials: Partial<PartCredentialType> = {}
    ) => {
      const sourceMimeType = getMimeTypeFromSourceUrl(plainSourceUrl);
      const playlistBuilder = mimeTypeToPlaylistBuilderMap[sourceMimeType];
      const hasBuilder = Boolean(playlistBuilder);
      const hasCredentials = Object.keys(partCredentials).length > 0;
      const sourceUrlIsTranscoded = plainSourceUrl.includes(
        '/sessions/transcoded/'
      );

      const sourceUrl =
        !hasBuilder || !hasCredentials
          ? plainSourceUrl
          : await playlistBuilder(plainSourceUrl, partCredentials);

      resolve({
        plainSourceUrl,
        sourceUrl,
        sourceMimeType,
        partCredentials,
        sourceUrlIsTranscoded,
      });
    };

  return new Promise(resolve => {
    getUrlHandler(
      videoId,
      password,
      parsePlaylistInfo(resolve),
      forceOriginal,
      supportedMimeTypes
    );
  });
};
